/**
 * Operations Permission checks
 * Hook to check if the current user has specific operations permissions
 */

import { useSession, useHasAnyRole } from '@/hooks/useSession';

const ADMIN_ROLES = ['full_admin', 'director'] as const;
const OPS_ROLES = ['full_admin', 'director', 'operations_manager'] as const;

const TASK_PERMISSIONS = {
  create: 'operations.tasks.create',
  edit: 'operations.tasks.edit',
  delete: 'operations.tasks.delete',
  assign: 'operations.tasks.assign',
} as const;

/**
 * Hook for checking user permissions for operations tasks and projects
 * Returns boolean flags for each permission type
 */
export function useOperationsPermissions() {
  const { user } = useSession();
  const isAdmin = useHasAnyRole([...ADMIN_ROLES]);
  const isOpsRole = useHasAnyRole([...OPS_ROLES]);
  const permissionStrings = user?.permission_strings ?? [];

  const hasPermission = (permission: string) => (
    isAdmin || permissionStrings.includes(permission)
  );

  // Task permissions
  const canCreateTasks = hasPermission(TASK_PERMISSIONS.create);
  const canEditTasks = hasPermission(TASK_PERMISSIONS.edit);
  const canDeleteTasks = hasPermission(TASK_PERMISSIONS.delete);
  const canAssignTasks = hasPermission(TASK_PERMISSIONS.assign);

  // Project permissions
  const canCreateProjects = isOpsRole;
  const canEditProjects = isOpsRole;
  const canDeleteProjects = isAdmin;
  const canAssignProjects = isOpsRole;

  return {
    // Task permissions
    canCreateTasks,
    canEditTasks,
    canDeleteTasks,
    canAssignTasks,
    // Project permissions
    canCreateProjects,
    canEditProjects,
    canDeleteProjects,
    canAssignProjects,
  };
}
